 <script lang="ts">
    import Vue from "vue";
    import { Component, mixins } from 'vue-facing-decorator';
    import { MonthPicker } from 'vue-month-picker'
    import { MonthPickerInput } from 'vue-month-picker'
     import CorporateScenariosQueries from "@Quest/CorporateScenarios/Domain/queries-vue-component";
     import { SchedulingSettingsReadModel } from "@Quest/CorporateScenarios/Domain/readmodels";
     import CorporateScenariosCommands from "@Quest/CorporateScenarios/Domain/commands-vue-component";
     import { MonthYear, MonthYearHelpers } from "@common/types";
     import { OptimizeCorporateScenarioScheduleCommandV1 } from "@Quest/CorporateScenarios/Domain/commands-core";
     import { ICommandBase } from "@Quest/cqrs";
     import { SchedulerDialogViewModel } from "@Quest/CorporateScenarios/Components/SchedulerDialog";
     import { MonthPickerDate } from "@common/Components/MonthPickerDialog";


     @Component({
         components: {
             MonthPicker, MonthPickerInput
         },
         props: {
             viewModel: { type: Object, required: true }
         },
         watch: {
             opened: 'onOpenedChanged'
         }
     })
    export default class SchedulerDialog extends mixins(Vue, CorporateScenariosQueries, CorporateScenariosCommands)
    {
        get vm(): SchedulerDialogViewModel { return this.$props.viewModel as SchedulerDialogViewModel; }
        get opened(): boolean { return this.vm?.opened ?? false; }

        get workspaceId(): number { return Number.parseInt(this.$route.params.workspaceId); }
        get strategyId() { return this.$route.params.strategyId; }
        get scenarioId() { return this.$route.params.scenarioId; }

        rm: SchedulingSettingsReadModel = null;
        isScheduling: boolean = false;
        show1: boolean = false;
        show2: boolean = false;
        
        scenarioStartDate: Date = null;
        scenarioEndDate: Date = null;
        schedulingResult: any = null;

        minStartDate: MonthPickerDate = null;
        minStartDateFrom: Date = null;
        minStartDateTo: Date = null;

        maxStartDate: MonthPickerDate = null;
        maxStartDateFrom: Date = null;
        maxStartDateTo: Date = null;

        get defaultYear1(): number { return this.minStartDate.year; }
        get defaultMonth1(): number { return this.minStartDate.monthIndex; }

        get defaultYear2(): number { return this.maxStartDate.year; }
        get defaultMonth2(): number { return this.maxStartDate.monthIndex; }

        onMinStartDateChange(date: MonthPickerDate) 
        {
            this.minStartDate = date;
        }

        onMaxStartDateChange(date: MonthPickerDate) 
        {
            this.maxStartDate = date;
        }

        //@Watch('opened')
        onOpenedChanged(val: boolean, oldVal: boolean)
        {
            if (val === true)
            {
                this.rm = null;                
                this.runSchedulingSettingsQueryV1(this.workspaceId, this.strategyId, this.scenarioId);
            }
            else
            {
                this.rm = null;
                this.isScheduling = false;
                this.schedulingResult = null;
            }
        }


        handleOptimizeCorporateScenarioScheduleCommand()
        {
            this.isScheduling = true;
            this.schedulingResult = null;

            const minStartDate = MonthYearHelpers.build(this.minStartDate.year, this.minStartDate.monthIndex);
            const maxStartDate = MonthYearHelpers.build(this.maxStartDate.year, this.maxStartDate.monthIndex);

            this.invokeOptimizeCorporateScenarioScheduleCommandV1(this.workspaceId, this.strategyId, this.scenarioId, minStartDate, maxStartDate);
        }

        close()
        {
            this.vm.close();
        }

        onSetModel(model: any, modelName: string) 
        {
            if (modelName == "SchedulingSettingsReadModel")
            {
                const rm = model as SchedulingSettingsReadModel;
                this.scenarioStartDate = new Date(rm.ScenarioStartDateStr);
                this.scenarioEndDate = new Date(rm.ScenarioEndDateStr);

                const minStartDate = new Date(rm.MinStartDateStr);
                this.minStartDate = { year: minStartDate.getFullYear(), monthIndex: minStartDate.getMonth() + 1 };
                this.minStartDateFrom = new Date(this.scenarioStartDate.getFullYear(), this.scenarioStartDate.getMonth(), 0, 0, 0, 0, 0);
                this.minStartDateTo = new Date(this.scenarioEndDate.getFullYear(), this.scenarioEndDate.getMonth() + 1, 0);

                const maxStartDate = new Date(rm.MaxStartDateStr);
                this.maxStartDate = { year: maxStartDate.getFullYear(), monthIndex: maxStartDate.getMonth() + 1 };
                this.maxStartDateFrom = new Date(this.scenarioStartDate.getFullYear(), this.scenarioStartDate.getMonth(), 0, 0, 0, 0, 0);
                this.maxStartDateTo = new Date(this.scenarioEndDate.getFullYear(), this.scenarioEndDate.getMonth() + 1, 0);

                this.rm = rm;
            }
        }

        onCommandSucceed(cmd: ICommandBase, correlationId: string, result: any)
        {
            if (OptimizeCorporateScenarioScheduleCommandV1.matchType(cmd))
            {
                this.schedulingResult = result;
                this.isScheduling = false;
            }
        }

        onCommandFailed(status: any)
        {
            if (OptimizeCorporateScenarioScheduleCommandV1.matchType(status.Command))
            {
                this.schedulingResult = null;
                this.isScheduling = false;

                alert('Sorry, something went wrong :(');
                console.log(status);
            }
        }
    }

</script>

<template>
    <div class="modal-mask" v-if="opened === true">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            Scheduler
                        </h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body" style="height: 400px;">
                        <div v-if="rm != null && isScheduling === true">
                            <p class="text-center text-primary my-5">
                                <i class="fa-solid fa-robot fa-beat-fade" style="font-size: 120px;"></i>
                            </p>
                        </div>
                        <div v-if="rm != null && isScheduling === false && schedulingResult != null">
                            <p>Analyzed (unique) portfolios: {{schedulingResult.NumberOfUniquePortfolios}}</p>
                            <p>Found (valid) portfolios: {{schedulingResult.NumberOfValidPortfolios}}</p>
                        </div>

                        <div v-if="rm && isScheduling === false && schedulingResult == null">
                            <div class="form-group">
                                <label>Earliest start date:</label>
                                <div class="input-group">
                                    <input :value="`${minStartDate.month}, ${minStartDate.year}`" type="text" class="form-control pointer bg-white" readonly @click="show1 = !show1" />
                                    <div class="input-group-append">
                                        <button class="btn btn-outline-secondary" type="button" @click="show1 = !show1">
                                            <i class="fa-regular fa-calendar" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                </div>
                                <month-picker v-show="show1 === true"
                                              @change="onMinStartDateChange"
                                              :min-date="minStartDateFrom"
                                              :max-date="minStartDateTo"
                                              :default-month="defaultMonth1"
                                              :default-year="defaultYear1"></month-picker>

                            </div>
                            <div class="form-group">
                                <label>Latest start date:</label>
                                <div class="input-group">
                                    <input :value="`${maxStartDate.month}, ${maxStartDate.year}`" type="text" class="form-control pointer bg-white" readonly @click="show2 = !show2" />
                                    <div class="input-group-append">
                                        <button class="btn btn-outline-secondary" type="button" @click="show2 = !show2">
                                            <i class="fa-regular fa-calendar" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                </div>
                                <month-picker v-show="show2 === true"
                                              @change="onMaxStartDateChange"
                                              :min-date="maxStartDateFrom"
                                              :max-date="maxStartDateTo"
                                              :default-month="defaultMonth2"
                                              :default-year="defaultYear2"></month-picker>

                            </div>
                        </div>


                    </div>

                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary" :disabled="isScheduling === true">Cancel</button>
                        <button @click="handleOptimizeCorporateScenarioScheduleCommand" type="button" class="btn btn-primary" :disabled="isScheduling === true">
                            <i class="fa-solid fa-robot fa-bounce mr-1"></i> Schedule now
                        </button>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>      


<style scoped>
</style> 